<template>
  <entity-edit-dialog
    @submit="submit"
    @close="close"
    v-model="visible"
    ref="createSupplierDialog"
    :dialog-title="dialogTitle"
    :submitting="submitting"
  >
    <template v-slot:contentCard>
      <v-card-text>
        <div class="my-5" v-if="error && !submitting">
          <p class="alertText">{{ $t('common.errors.error') }}</p>
          <p>{{ error }}</p>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold required">{{ $t('supplier.name') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplierFormData.name"
                ref="nameInput"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('validation.required')]"
                required
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('supplier.email') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplierFormData.email"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
                validate-on-blur
                :rules="[buildEmailValidator()]"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('supplier.phoneNumber') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplierFormData.phoneNumber"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('supplier.webSite') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplierFormData.webSite"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('supplier.address') }}</span>
            </v-col>
            <v-col cols="12" sm="8">
              <v-textarea
                v-model="supplierFormData.address"
                rows="3"
                outlined
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('supplier.description') }}</span>
            </v-col>
            <v-col cols="12" sm="8">
              <v-textarea
                v-model="supplierFormData.description"
                rows="3"
                outlined
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="mt-5">
          <v-row align="center" dense>
            <v-col cols="6" sm="3">
              <span class="span-bold">{{ $t('supplier.contactPersons') }}</span>
            </v-col>
          </v-row>
        </div>

        <div
          v-if="
            supplierFormData.contactPersons.length > 0 &&
            !$vuetify.breakpoint.xs
          "
        >
          <v-row align="center" dense>
            <v-col sm="4">
              <span class="span-bold required">{{ $t('supplier.name') }}</span>
            </v-col>
            <v-col sm="3">
              <span class="span-bold">{{ $t('supplier.phoneNumber') }}</span>
            </v-col>
            <v-col sm="4">
              <span class="span-bold">{{ $t('supplier.email') }}</span>
            </v-col>
          </v-row>
        </div>

        <v-row
          dense
          v-for="(contact, i) in supplierFormData.contactPersons"
          :key="i"
        >
          <v-col v-if="$vuetify.breakpoint.xs" cols="12">
            <span class="span-bold required">{{ $t('supplier.name') }}</span>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="contact.name"
              :clearable="true"
              outlined
              dense
              hide-details="auto"
              autocomplete="false"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            />
          </v-col>

          <v-col v-if="$vuetify.breakpoint.xs" cols="12">
            <span class="span-bold">{{ $t('supplier.phoneNumber') }}</span>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="contact.phoneNumber"
              :clearable="true"
              outlined
              dense
              hide-details="auto"
              autocomplete="false"
            />
          </v-col>

          <v-col v-if="$vuetify.breakpoint.xs" cols="12">
            <span class="span-bold">{{ $t('supplier.email') }}</span>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="contact.email"
              :clearable="true"
              outlined
              dense
              hide-details="auto"
              autocomplete="false"
              validate-on-blur
              :rules="[buildEmailValidator()]"
            />
          </v-col>
          <v-btn
            icon
            :title="$t('entitySelect.devete')"
            @click="removeContact(i)"
          >
            <v-icon>mdi-minus-circle-outline</v-icon>
          </v-btn>
        </v-row>

        <div class="mt-2 mb-5 ml-2">
          <v-row dense>
            <v-btn small @click="addContact">
              <span>{{ $t('actions.add') }}</span>
            </v-btn>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12">
              <span class="span-bold">{{
                $t('supplier.partOrderTemplateTitle')
              }}</span>
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="11">
              <v-text-field
                v-model="supplierFormData.title"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
                :placeholder="
                  $t('supplier.partOrderTemplateHeadLinePlaceholder')
                "
              />
            </v-col>
          </v-row>
        </div>
        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="11">
              <v-textarea
                v-model="supplierFormData.message"
                rows="3"
                outlined
                hide-details="auto"
                autocomplete="false"
                :placeholder="$t('supplier.partOrderTemplateTextPlaceholder')"
              />
            </v-col>
          </v-row>
        </div>
        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12">
              <span class="span-bold">{{
                $t('supplier.partOrderTemplateText')
              }}</span>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import i18n from '@/i18n';
import SupplierService from '@/services/SupplierService';
import { buildEmailValidator } from '@/utils/ValidatorBuilders';

// WARNING: Do not use this as an example on how to do anything


function createData() {
  return {
    name: null,
    email: '',
    phoneNumber: '',
    webSite: '',
    address: '',
    description: '',
    contactPersons: [],
    title: '',
    message: '',
  };
}
export default {
  name: 'SupplierFormDialog',

  components: {
    EntityEditDialog,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    supplierData: {
      type: Object,
    },
  },

  data: () => ({
    supplierFormData: createData(),
    submitting: false,
    error: null,
  }),

  computed: {
    mapToItem(supplier) {
      return {
        text: this.$options.filters.supplier(supplier),
        value: supplier,
      };
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      if (this.supplierFormData?.id) {
        return i18n.t('supplier.dialog.titleEdit');
      } else {
        return i18n.t('supplier.dialog.titleAdd');
      }
    },
  },

  methods: {
    buildEmailValidator,

    addContact() {
      this.supplierFormData.contactPersons.push({
        name: null,
        phoneNumber: '',
        email: '',
      });
    },

    removeContact(index) {
      this.supplierFormData.contactPersons.splice(index, 1);
    },

    close() {
      this.supplierFormData = createData();
      this.submitting = false;
      this.error = false;

      this.$emit('dialogClosed');
    },

    submit() {
      if (this.submitting) {
        return;
      }
      this.error = null;

      if (!this.$refs.createSupplierDialog.$refs.form.validate()) {
        this.$refs.createSupplierDialog.showFormHasValidationErrorsMessage(
          i18n.t('plannedService.requiredInformationMissing'),
        );
        return;
      }

      const supplier = {
        name: this.supplierFormData.name,
        email: this.supplierFormData.email,
        phoneNumber: this.supplierFormData.phoneNumber,
        webSite: this.supplierFormData.webSite,
        address: this.supplierFormData.address,
        description: this.supplierFormData.description,
        partOrderTemplate: {
          title: this.supplierFormData.title,
          message: this.supplierFormData.message,
        },
        contactPersons: this.supplierFormData.contactPersons.map((contact) => ({
          name: contact.name,
          phoneNumber: contact.phoneNumber,
          email: contact.email,
        })),
      };

      this.submitting = true;
      SupplierService.addSupplier(supplier)
        .then((result) => {
          this.$emit('selectedSupplier', result.data);
          this.close();
        })
        .catch((err) => {
          this.error =
            err.response.data.message ||
            err.message ||
            err.toString() ||
            'Some Network Error';

          this.$refs.nameInput.focus();
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.required:after {
  content: ' *';
  color: red;
}

.span-bold {
  font-weight: bolder;
}

.alertText {
  font-weight: bolder;
  color: red;
}
</style>
